import {
    Directive,
    OnInit,
    OnDestroy,
    Inject,
    Optional,
    Input,
    Injector,
    ComponentFactoryResolver,
    ViewContainerRef,
    ChangeDetectorRef,
    Renderer2,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ADS_BANNER, ADS_PARAMS } from '@app/ads/tokens';
import { AdsService } from '@app/ads/services/ads.service';
import * as Banners from '../components';
import { environment } from '@env/environment';
import { Helper } from '@app/core/helper';
import { StateService } from '@app/core/services/state.service';

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';

@Directive({
    selector: '[ads]',
})
export class AdsDirective implements OnInit, OnDestroy {
    private d$: Subject<boolean> = new Subject();
    private cls!: string[] | null;
    public b$: BehaviorSubject<any> = new BehaviorSubject(void 0);
    environment: any = environment;

    @Input('ads')
    cond!: boolean;

    @Input('adsPos')
    pos!: string;

    @Input('adsCls')
    set _cls(cls: string | string[]) {
        if ((typeof cls === 'string' || cls instanceof String) && cls.length) {
            this.cls = cls.split(',');
        } else if (Array.isArray(cls) && cls.length) {
            this.cls = cls;
        } else {
            this.cls = null;
        }
    }

    @Input('adsInd')
    ind!: number;
    constructor(
        @Optional()
        @Inject(ADS_PARAMS)
        public pp: Subject<any>,
        @Inject(DOCUMENT)
        private doc: Document,
        private srv: AdsService,
        private cdr: ChangeDetectorRef,
        private cfr: ComponentFactoryResolver,
        private vc: ViewContainerRef,
        private ss: StateService,
        private r: Renderer2
    ) {

    }

    ngOnInit(): void {
        this.cond &&
            this.srv
                .bind(this)
                .pipe(
                    takeUntil(this.d$))
                .subscribe(([banners, allBanners]: any) => {
                    if (this.pos === 'in_body') {
                        const bnrs: any = this.resolve(
                            this.filterByDevice(banners),
                            allBanners
                        );
                        if (bnrs?.length) {
                            this.createInBody(bnrs);
                            this.b$.next(bnrs);
                        }
                    } else {
                        const bnr: any = this.resolve(
                            this.filterByDevice(banners),
                            allBanners
                        );
                        if (this.b$.value !== bnr) {
                            this.vc.clear();
                            if (bnr) {
                                const clsNm = 'B' + bnr.t + 'Component';
                                const injector: Injector = Injector.create({
                                    providers: [
                                        {
                                            provide: ADS_BANNER,
                                            useValue: bnr,
                                        },
                                    ],
                                });
                                const cls: any =
                                    Banners[clsNm as keyof typeof Banners];
                                const componentFactory =
                                    this.cfr.resolveComponentFactory(cls);
                                const componentRef = this.vc.createComponent(
                                    componentFactory,
                                    void 0,
                                    injector
                                );
                                const node =
                                    componentRef.location.nativeElement;
                                if (this.cls) {
                                    for (const cls of this.cls) {
                                        this.r.addClass(node, cls);
                                    }
                                }
                                this.cd();
                            }
                            this.b$.next(bnr);
                        }
                    }
                });
    }

    ngOnDestroy(): void {
        this.d$.next(true);
        this.d$.complete();
        this.srv.unbind(this);
    }

    cd(): void {
        this.cdr.detectChanges();
    }

    ord(): number {
        const p: any = 0;
        if (this.pos === 'in_list') {
            return p + this.ind;
        }
        return p;
    }

    normPos(): string {
        return this.pos.indexOf('{n}') === -1
            ? this.pos
            : this.pos.replace('{n}', '' + this.ind);
    }

    private resolve(banners: any[], allBanners: any[]): any {
        if (this.b$.value && (this.pos !== 'in_body')) {
            for (let banner of banners) {
                if (banner.location) {
                    continue;
                }
                if (this.b$.value.id === banner.id) {
                    banner.location = this.pos;
                    return this.b$.value;
                }
            }
        }
        const max = 1000;
        const bnrs = [];
        let weight = 0;
        for (let banner of banners) {
            // проверяем чтобы не было выведено другого баннера из данной категории
            let br = false;
            for (let _banner of allBanners) {
                if (_banner.cid === banner.cid && _banner.location) {
                    br = true;
                    break;
                }
            }
            if (br) {
                continue;
            }
            const startWeight = weight;
            weight += banner.w;
            bnrs.push([banner, startWeight, weight]);
        }
        if (this.pos === 'in_body') {
            if (!bnrs.length) {
                return void 0;
            }
            const newBanners = bnrs.map(b =>{
                b[0].location = this.pos;
                return b[0];
            });
            if (Array.isArray(this.b$.value) && (this.b$.value.length === newBanners.length)) {
                for (const b1 of this.b$.value) {
                    if (newBanners.findIndex(x => x.id === b1.id) === -1) {
                        return newBanners;
                    }
                }
                return this.b$.value;
            }
            return newBanners;
        }
        const rnd = Math.floor(Math.random() * max); // 0..max
        const sel = (rnd / max) * weight;
        for (let [banner, startWeight, endWeight] of bnrs) {
            if (startWeight <= sel && endWeight > sel) {
                banner.location = this.pos;
                return banner;
            }
        }
        return void 0;
    }

    filterByDevice(bns: any[]) {
        let isMob = this.ss.rs$.value.clientWidth > 480 ? false : true;
        if (isMob) {
            return bns.filter((b) => {
                return (
                    !b.devs?.length || b.devs.find((el: string) => el === 'mob')
                );
            });
        } else {
            return bns.filter((b) => {
                return (
                    !b.devs?.length || b.devs.find((el: string) => el === 'pc')
                );
            });
        }
    }

    createInBody(allBanners: any) {
        this.vc.clear();
        const clsNm = 'B2InbodyComponent';
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: ADS_BANNER,
                    useValue: allBanners,
                },
            ],
        });
        const cls: any = Banners[clsNm as keyof typeof Banners];
        const componentFactory = this.cfr.resolveComponentFactory(cls);
        const componentRef = this.vc.createComponent(
            componentFactory,
            void 0,
            injector
        );
        const node = componentRef.location.nativeElement;
        if (this.cls) {
            for (const cls of this.cls) {
                this.r.addClass(node, cls);
            }
        }

        this.cd();
    }
}
